import React, { useEffect, useState } from "react";
import { Briefcase, Cloud, Megaphone, Bot, Sparkles, Bookmark, ClipboardList, Bell } from "lucide-react";
import api from "@/lib/api";
import AutoApplyPanel from "@/components/AutoApplyPanel";
import JobCard from "@/components/jobs/JobCard";
import JobFilters from "@/components/jobs/JobFilters";
import JobAlertsPanel from "@/components/jobs/JobAlertsPanel";
import MyApplicationsList from "@/components/jobs/MyApplicationsList";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const TRACKS = [
  { id: "all", label: "All Tracks", icon: Briefcase, accent: "slate" },
  { id: "data_cloud", label: "Data Cloud", icon: Cloud, accent: "indigo" },
  { id: "sfmc", label: "SFMC", icon: Megaphone, accent: "teal" },
  { id: "mc_next", label: "MC Next", icon: Bot, accent: "fuchsia" },
];

const TRACK_ACTIVE_CLASSES = {
  slate: "bg-white/15 text-white border border-white/20",
  indigo: "bg-indigo-500/20 text-indigo-300 border border-indigo-400/30",
  teal: "bg-teal-500/20 text-teal-300 border border-teal-400/30",
  fuchsia: "bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-400/30",
};

export default function JobBoardPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [track, setTrack] = useState("all");
  const [filters, setFilters] = useState({ q: "", location: "", job_type: "", experience_level: "" });
  const [recommended, setRecommended] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);
  const [savedJobs, setSavedJobs] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("browse");

  useEffect(() => {
    setLoading(true);
    const params = { track, ...filters };
    Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
    api.get("/jobs", { params }).then((res) => setJobs(res.data)).finally(() => setLoading(false));
  }, [track, filters]);

  useEffect(() => {
    if (activeTab === "recommended" && recommended.length === 0) {
      setLoadingRecommended(true);
      api.get("/jobs/recommended").then((r) => setRecommended(r.data)).finally(() => setLoadingRecommended(false));
    }
    if (activeTab === "saved") {
      setLoadingSaved(true);
      api.get("/jobs/me/saved").then((r) => setSavedJobs(r.data)).finally(() => setLoadingSaved(false));
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleSave = (jobId, saved) => {
    setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, is_saved: saved } : j)));
    setRecommended((prev) => prev.map((j) => (j.id === jobId ? { ...j, is_saved: saved } : j)));
    if (!saved) setSavedJobs((prev) => prev.filter((j) => j.id !== jobId));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="job-board-page">
      <div className="mb-6">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">Job Ready Learning</span>
        <h1 className="font-heading text-3xl font-semibold text-white mt-2">Salesforce Job Board</h1>
        <p className="text-slate-400 text-sm mt-1">Real openings across Data Cloud, SFMC & MC Next for certified Cloudivo learners.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="job-board-tabs">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="browse" data-testid="job-board-tab-browse">Browse Jobs</TabsTrigger>
          <TabsTrigger value="recommended" data-testid="job-board-tab-recommended">
            <Sparkles className="w-3.5 h-3.5 mr-1.5 inline" /> Recommended
          </TabsTrigger>
          <TabsTrigger value="saved" data-testid="job-board-tab-saved">
            <Bookmark className="w-3.5 h-3.5 mr-1.5 inline" /> Saved
          </TabsTrigger>
          <TabsTrigger value="applications" data-testid="job-board-tab-applications">
            <ClipboardList className="w-3.5 h-3.5 mr-1.5 inline" /> My Applications
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="job-board-tab-alerts">
            <Bell className="w-3.5 h-3.5 mr-1.5 inline" /> Alerts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="browse" data-testid="job-board-content-browse">
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="job-board-track-filter">
            {TRACKS.map((t) => {
              const Icon = t.icon;
              const active = track === t.id;
              return (
                <button
                  key={t.id} onClick={() => setTrack(t.id)} data-testid={`job-board-track-${t.id}`}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
                    active ? TRACK_ACTIVE_CLASSES[t.accent] : "bg-white/5 text-slate-400 hover:text-white border border-transparent"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              );
            })}
          </div>

          <JobFilters filters={filters} setFilters={setFilters} />

          <AutoApplyPanel />

          {loading ? (
            <div className="flex items-center justify-center py-16" data-testid="jobs-loading">
              <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="glass-panel rounded-2xl p-10 text-center" data-testid="jobs-empty-state">
              <Briefcase className="w-8 h-8 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">No job listings match your filters — try widening your search.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((j) => <JobCard key={j.id} job={j} onToggleSave={handleToggleSave} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="recommended" data-testid="job-board-content-recommended">
          {loadingRecommended ? (
            <div className="flex items-center justify-center py-16"><div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" /></div>
          ) : recommended.length === 0 ? (
            <div className="glass-panel rounded-2xl p-10 text-center" data-testid="recommended-jobs-empty">
              <Sparkles className="w-8 h-8 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">Save your resume in the Auto-Apply panel (Browse Jobs tab) to get AI-matched recommendations here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recommended.map((j) => <JobCard key={j.id} job={j} onToggleSave={handleToggleSave} showMatchScore />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="saved" data-testid="job-board-content-saved">
          {loadingSaved ? (
            <div className="flex items-center justify-center py-16"><div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" /></div>
          ) : savedJobs.length === 0 ? (
            <div className="glass-panel rounded-2xl p-10 text-center" data-testid="saved-jobs-empty">
              <Bookmark className="w-8 h-8 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">No saved jobs yet — tap the bookmark icon on any job card to save it here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {savedJobs.map((j) => <JobCard key={j.id} job={j} onToggleSave={handleToggleSave} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="applications" data-testid="job-board-content-applications">
          <MyApplicationsList />
        </TabsContent>

        <TabsContent value="alerts" data-testid="job-board-content-alerts">
          <JobAlertsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
